import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { playDing } from '../utils/playDing';
import { parseMoney } from '../utils/auctionMoney';

const SocketContext = createContext(null);

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? '' : 'http://localhost:4000');

function coerceBidEvent(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  if (next.amount != null) next.amount = parseMoney(next.amount);
  if (next.newBid != null) next.newBid = parseMoney(next.newBid);
  return next;
}

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const userRef = useRef(user);
  const [connected, setConnected] = useState(false);

  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    // Create socket connection
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect', () => {
      setConnected(true);
      // Join personal room for outbid notifications
      if (user?.id) {
        socketRef.current.emit('join_user', user.id);
      }
    });

    socketRef.current.on('disconnect', () => setConnected(false));
    socketRef.current.on('connect_error', () => setConnected(false));

    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  // Ding on incoming messages from other users
  useEffect(() => {
    if (!socketRef.current) return;
    const handler = (msg) => {
      if (msg.sender_id !== userRef.current?.id) playDing();
    };
    socketRef.current.on('new_message', handler);
    return () => socketRef.current?.off('new_message', handler);
  }, [connected]);

  // Join personal room when user logs in
  useEffect(() => {
    if (user?.id && socketRef.current?.connected) {
      socketRef.current.emit('join_user', user.id);
    }
  }, [user?.id]);

  const joinAuction  = (listingId) => socketRef.current?.emit('join_auction', listingId);
  const leaveAuction = (listingId) => socketRef.current?.emit('leave_auction', listingId);

  const joinListingView  = (listingId) => socketRef.current?.emit('join_listing_view', listingId);
  const leaveListingView = (listingId) => socketRef.current?.emit('leave_listing_view', listingId);
  const onListingViewerCount = (cb) => {
    socketRef.current?.on('listing_viewer_count', cb);
    return () => socketRef.current?.off('listing_viewer_count', cb);
  };

  const onNewBid = (cb) => {
    const wrapped = (data) => cb(coerceBidEvent(data));
    socketRef.current?.on('new_bid', wrapped);
    return () => socketRef.current?.off('new_bid', wrapped);
  };

  const onOutbid = (cb) => {
    const wrapped = (data) => cb(coerceBidEvent(data));
    socketRef.current?.on('outbid', wrapped);
    return () => socketRef.current?.off('outbid', wrapped);
  };

  // Messaging methods
  const joinConversation = (conversationId) => socketRef.current?.emit('join_conversation', conversationId);
  const leaveConversation = (conversationId) => socketRef.current?.emit('leave_conversation', conversationId);

  const onNewMessage = (cb) => {
    socketRef.current?.on('new_message', cb);
    return () => socketRef.current?.off('new_message', cb);
  };

  const emitTyping = (conversationId, isTyping) => {
    socketRef.current?.emit('user_typing', { conversationId, userId: user?.id, isTyping });
  };

  const onUserTyping = (cb) => {
    socketRef.current?.on('user_typing', cb);
    return () => socketRef.current?.off('user_typing', cb);
  };

  const onUserStatus = (cb) => {
    socketRef.current?.on('user_status', cb);
    return () => socketRef.current?.off('user_status', cb);
  };

  const markMessagesRead = (conversationId, messageIds) => {
    socketRef.current?.emit('mark_read', { conversationId, messageIds });
  };

  const onMessagesRead = (cb) => {
    socketRef.current?.on('messages_read', cb);
    return () => socketRef.current?.off('messages_read', cb);
  };

  const onNewNotification = (cb) => {
    socketRef.current?.on('new_notification', cb);
    return () => socketRef.current?.off('new_notification', cb);
  };

  return (
    <SocketContext.Provider value={{ connected, joinAuction, leaveAuction, onNewBid, onOutbid, joinConversation, leaveConversation, onNewMessage, emitTyping, onUserTyping, onUserStatus, markMessagesRead, onMessagesRead, socket: socketRef.current, joinListingView, leaveListingView, onListingViewerCount, onNewNotification }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
